import React, { useMemo } from 'react';
import Select from "react-select";
import "../../../assets/css/ArticulationForm.css";
import { getArticulationData, getFormattedArticulationPrice, useArticulationForm } from '../../../utils/Articulation-Util';
import CommonCourseComponents from '../../course/CommonCourseComponents';
import { lblOtherText } from './Consts';

/**
 * Step 2. Review and Submit
 * 3 different types of views:
 * 1. Editable - The Articulation has not been submitted.
 * 2. Closed and disabled, but still viewable.
 * 3. Submitted.
 * @param {object} props
 */
function ArticulationReviewAndSubmit({
    articulationReadOnly,
    articulationOptionSelected,
    campusList,
    disabled,
    learningMaterialsList,
    payloadData,
    qualificationDetails,
    schoolTypes,
    setCurrentStep,
    submitted
}) {
    const {
        isOther,
        categoryColor,
        credits,
        description,
        name,
        saqaId,
        school,
        type,
        url
    } = getArticulationData(articulationOptionSelected, articulationReadOnly);

    const schoolValue = useMemo(() => {
        if (schoolTypes?.length > 0) {
            return schoolTypes.find(x => x.value === school);
        }
        return '';
    }, [school, schoolTypes]);

    const { disableInputs, formik, isLoading } = useArticulationForm(
        articulationReadOnly,
        articulationOptionSelected,
        disabled,
        qualificationDetails,
        payloadData,
        submitted,
        isOther
    );

    const { handleSubmit, errors, touched, setFieldValue, values, handleBlur } = formik;
    const learningMethodValue = learningMaterialsList.filter(item => item.value === values.learningMethod);
    const campusValue = useMemo(() => {
        return campusList?.find(item => item.value === values.campus) || null
    }, [campusList, values]);

    const onSubmitProps = (!submitted && !disabled) && { onSubmit: handleSubmit };

    const bgStyleProps = categoryColor && { "--bg-category-color": categoryColor };
    const inputDisabledSelectorClass = disableInputs ? " isDisabled" : "";

    return (
        <form className='articulationForm reviewAndSubmit' noValidate {...onSubmitProps}>
            {!isOther && articulationOptionSelected && <div className="base-card">
                <h4 className="ao-header" style={bgStyleProps}>{schoolValue?.label ?? ''}</h4>
                <div className="ao-content">
                    <div className="ao-badge-row">
                        <span>{CommonCourseComponents({ degree: type })}</span>
                    </div>

                    <h2 className="ao-title">{name}</h2>


                    <div className="ao-info-row">
                        <div className="ao-info-item"><i class="fal fa-wallet"></i>From <strong>{getFormattedArticulationPrice(articulationOptionSelected)}</strong> (T&C)</div>
                        <div className="ao-info-item"><i class="fal fa-info-circle"></i>SAQA ID {saqaId} {credits} Credits</div>
                        <div className="ao-info-item"><i class="fal fa-clock"></i>Earliest Start Date: {payloadData.registration?.start_date ?? ''}</div>
                    </div>


                    {description && <p className="ao-description" dangerouslySetInnerHTML={{ __html: description }}></p>}

                    <div>
                        <button style={bgStyleProps} title="Learn More" className="btn btn-more" onClick={(e) => { e.preventDefault(); window.open(url, "_blank") }}>
                            Learn More
                        </button>
                    </div>
                </div>
            </div>}
            {isOther && (
                <div className="base-card otherOption">
                    <div>{lblOtherText}</div>
                </div>
            )}
            <div className="review-section base-card">
                <h2 className="ao-title">Review and Submit</h2>
                <p className='ao-description'>Please confirm your preferred method of learning and the campus you wish to attend.</p>
                <div className="form-icon-group">
                    <div className="form-icon-group-column">
                        <div className="form-label">
                            Learning Method <span>*</span>
                        </div>
                        {learningMaterialsList?.length > 0 && <Select
                            className={`form-control custom-select-box${(errors.learningMethod && touched.learningMethod ? " is-invalid" : "")}${inputDisabledSelectorClass}`}
                            menuPlacement="top"
                            name="learningMethod"
                            value={learningMethodValue}
                            onChange={(value) => { setFieldValue("learningMethod", value.value) }}
                            onBlur={handleBlur}
                            maxMenuHeight={175}
                            placeholder="Select Learning Method"
                            options={learningMaterialsList}
                            isDisabled={disableInputs}
                        />}
                    </div>
                    <div className="form-icon-group-column">
                        <div className="form-label">
                            Campus <span>*</span>
                        </div>
                        {campusList?.length > 0 && <Select
                            className={`form-control custom-select-box${(errors.campus && touched.campus ? " is-invalid" : "")}${inputDisabledSelectorClass}`}
                            menuPlacement="top"
                            name="campus"
                            value={campusValue}
                            onChange={(value) => { setFieldValue("campus", value.value) }}
                            onBlur={handleBlur}
                            maxMenuHeight={175}
                            placeholder="Select Campus"
                            options={campusList}
                            isDisabled={disableInputs}
                        />}
                    </div>
                </div>
                <div className="button-group">
                    {!submitted && <button
                        title="Modify Selection"
                        className="btn btn-submit btn-black"
                        type="button"
                        disabled={isLoading}
                        onClick={() => {
                            setCurrentStep(0);
                        }}>
                        <i className="fas fa-edit" />{disabled ? "Previous Step" : "Modify Selection"}
                    </button>}
                    <button
                        title="Submit Articulation"
                        className="btn btn-submit btn-primary"
                        type="submit"
                        disabled={disabled || submitted || isLoading}>
                        {isLoading && <i className="fas fa-cog fa-spin" />} {submitted ? 'Submitted' : 'Submit Articulation'}{<i className="fas fa-check-circle right-check" />}
                    </button>
                </div>
            </div>
        </form>
    );
}
export default ArticulationReviewAndSubmit;
